import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";

import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FormModel } from "src/app/_shared/utils/form-model";
import { ButtonComponent } from "../../../button/button.component";
import { SelectComponent } from "../../../form-fields/select/select.component";
import { OptionComponent } from "../../../form-fields/select/option.component";


export type Pagination = {
  page: number;
  perPage: number;
}

export type PaginationControls = FormModel<Pagination>


type PageOptionsToken = number | '...';

@Component({
  selector: 'tfoot[app-dt-paginate-foot]',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, SelectComponent, OptionComponent],
  styleUrls: ['dt-paginate-foot.component.scss'],
  templateUrl: 'dt-paginate-foot.component.html',
})
export class DtPaginateFootComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();

  @Input() total = 0;
  @Input() perPageOptions = [10, 50, 100, 500];

  @Input() paginate = new FormGroup<PaginationControls>({
    page: new FormControl(1, { nonNullable: true }),
    perPage: new FormControl(10, { nonNullable: true })
  });

  @Output() changed = new EventEmitter<Pagination>();

  ngOnInit(): void {
    this.validateInitialPerPage();
    this.listenPerPage();
    this.listenPage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private validateInitialPerPage() {
    const current = this.paginate.controls.perPage.value;
    if (!this.perPageOptions.includes(current)) {
      this.paginate.controls.perPage.setValue(this.perPageOptions[0], { emitEvent: false });
    }
  }

  get totalPages(): number {
    const per = this.paginate.controls.perPage.value || 1;
    return Math.max(1, Math.ceil(this.total / per));
  }

  get pages(): PageOptionsToken[] {
    const total = this.totalPages;
    const current = Number(this.paginate.controls.page.value);

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: PageOptionsToken[] = [];

    if (current <= 5) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total - 1, total);
      return pages;
    }

    if (current >= total - 2) {
      pages.push(1, 2);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1, 2);
    pages.push('...');
    pages.push(current - 1, current, current + 1);
    pages.push('...');
    pages.push(total - 1, total);

    return pages;
  }

  private listenPerPage() {
    this.paginate.controls.perPage.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.paginate.controls.page.setValue(1);
      });
  }

  private listenPage() {
    this.paginate.controls.page.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.emitCurrentValue();
    });
  }

  private emitCurrentValue() {
    const value = this.paginate.getRawValue() as Pagination;
    this.changed.emit(value);
  }

  onNextPage() {
    const paginateControl = this.paginate.controls.page;

    if (paginateControl.value < this.totalPages) {
      paginateControl.setValue(paginateControl.value + 1);
    }
  }

  onPreviousPage() {
    const paginateControl = this.paginate.controls.page;

    if (paginateControl.value > 1) {
      paginateControl.setValue(paginateControl.value - 1);
    }
  }

  onPageChange(page: PageOptionsToken) {
    if (typeof page === 'number') {
      this.paginate.controls.page.setValue(page);
    }
  }
}
